import { Filter } from '@fabric-ui/components';
import { barChartMaxSize, defaultTopSize } from '../../CustomDashboards/constants';
import {
  BarLegendPosition,
  BarSubType,
  BarTypeWidget,
  DisplayModeOptions,
  DonutTypeWidget,
  DwQueryRequest,
  LineLegendPosition,
  LineSubType,
  PieBasicLegendPosition,
  PieLegendStyles,
  PieSubType,
  SortDir,
  SplineTypeWidget,
  TileSubType,
  TileTypeWidget,
  TileWidgetVisualization,
  WidgetCategory,
} from '../../CustomDashboards/types';
import { AxisTickType, LabelFormatterOption } from '../../CustomDashboards/types/types';

export const getEntityOverTimeWidget = ({
  req,
  id = '',
  title = '',
}: {
  id?: string;
  req: DwQueryRequest;
  title?: string;
}): SplineTypeWidget => ({
  id,
  type: LineSubType.Spline,
  category: WidgetCategory.Line,
  requests: [req],
  definition: {
    title: {
      title,
    },
    coords: {
      w: 6,
      h: 4,
      x: 4,
      y: 0,
    },
    custom: {
      legend: {
        position: LineLegendPosition.Top,
      },
      chartPosition: { margin: { left: 0 } },
      fieldsPalette: {},
    },
  },
});

export const getTotalAssetsBrokenDownWidget = ({
  id = '',
  fieldName,
  filter,
}: {
  id?: string;
  fieldName: string;
  filter?: Filter;
}): DonutTypeWidget => ({
  id,
  definition: {
    title: {
      title: '',
    },
    coords: {
      x: 0,
      y: 0,
      h: 0,
      w: 0,
    },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: true,
        showPercentage: true,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [
    {
      filter,
      select: {
        dims: [
          {
            name: fieldName,
          },
        ],
        metrics: [
          {
            name: 'total_assets_granular',
            alias: 'total_assets_granular',
          },
        ],
      },
      groupBy: [fieldName],
      sorting: [
        {
          name: 'total_assets_granular',
          dir: SortDir.DESC,
        },
      ],
      top: {
        size: defaultTopSize,
        groupOthers: false,
        offset: 0,
      },
      projectionId: {
        name: 'uber_assets',
        builtIn: true,
      },
      distinct: false,
    },
  ],
});

export const getTotalAssetBreakDownBarWidget = ({
  id = '',
  fieldName,
  filter,
  showScroll = false,
}: {
  id?: string;
  fieldName: string;
  filter?: Filter;
  showScroll?: boolean;
}): BarTypeWidget => {
  const isSourceWidget = fieldName.endsWith('source_names');
  return {
    id,
    definition: {
      title: {
        title: '',
      },
      coords: {
        x: 0,
        y: 0,
        h: 4,
        w: 6,
      },
      custom: {
        legend: {
          position: BarLegendPosition.None,
        },
        hideLabel: true,
        rowsColumnSwitch: false,
        fieldsPalette: {},
        displayMode: isSourceWidget && showScroll ? DisplayModeOptions.SCROLL : DisplayModeOptions.FIT,
        labelFormatter: isSourceWidget ? LabelFormatterOption.SOURCE_ICONS : LabelFormatterOption.NONE,
        xAxisTickType: AxisTickType.Horizontal,
        chartPosition: { margin: { left: 50 } },
      },
    },
    category: WidgetCategory.Bar,
    type: BarSubType.BarVertical,
    requests: [
      {
        filter,
        select: {
          dims: [
            {
              name: isSourceWidget ? `explode(${fieldName})` : fieldName,
              // alias cant include a '.', we only want alias if its source name, because than we add the explode agg
              ...(isSourceWidget ? { alias: `explode(${fieldName})` } : {}),
            },
          ],
          metrics: [
            {
              name: 'total_assets_granular',
              alias: 'total_assets_granular',
            },
          ],
        },
        groupBy: [isSourceWidget ? `explode(${fieldName})` : fieldName],
        sorting: [
          {
            name: 'total_assets_granular',
            dir: SortDir.DESC,
          },
        ],
        top: {
          size: barChartMaxSize,
          groupOthers: false,
          offset: 0,
        },
        projectionId: {
          name: 'uber_assets',
          builtIn: true,
        },
        distinct: true,
        metricsFilterThreshold: 0,
      },
    ],
  };
};

export const getTileWidget = ({
  id = '',
  metricName,
  filter,
  title = '',
}: {
  id?: string;
  metricName: string;
  filter?: Filter;
  title?: string;
}): TileTypeWidget => ({
  id,
  definition: {
    title: {
      title,
    },
    coords: {
      x: 0,
      y: 0,
      h: 3,
      w: 4,
    },
    custom: {
      visualization: TileWidgetVisualization.None,
    },
  },
  category: WidgetCategory.Tile,
  type: TileSubType.Tile,
  requests: [
    {
      filter,
      select: {
        dims: [],
        metrics: [
          {
            name: metricName,
            alias: metricName,
          },
        ],
      },
      groupBy: [],
      sorting: [
        {
          name: metricName,
          dir: SortDir.DESC,
        },
      ],
      top: {
        size: 100,
        groupOthers: false,
        offset: 0,
      },
      projectionId: {
        name: 'uber_assets',
        builtIn: true,
      },
      distinct: false,
      metricsFilterThreshold: 0,
    },
  ],
});
